import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useWeb3 } from '../../context/Web3Context';
import { useTheme } from '../../context/ThemeContext';
import { Button } from '../ui/button';
import { LanguageSelector } from '../LanguageSelector';
import { NavLinkItem, ThemeButtons } from './NavbarParts';
import { NavbarWalletSection } from './NavbarWalletSection';
import {
    User, Menu, X, LayoutGrid, Trophy, Sparkles, Shield, Coins,
    Crown, Bot, Gift, Package, Users as UsersIcon, Send as SendIcon,
} from 'lucide-react';

const BET4PEER_LOGO = "https://customer-assets.emergentagent.com/job_83ddb6ac-1a43-447f-9a3b-123d64b2d59d/artifacts/pter9nuv_Logo%20futuriste%20Bet4Peer%20avec%20poigne%CC%81e%20de%20main.png";
const API_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_ADDRESS = "0xAf122740bD739B49d980a48Dfb6E0141cCE79338";

const NAV_LINKS = [
    { href: '/bets', labelKey: 'nav.bets', icon: LayoutGrid },
    { href: '/ai-bets', labelKey: 'nav.aiBets', icon: Bot },
    { href: '/quests', labelKey: 'nav.quests', icon: Gift },
    { href: '/social-trading', labelKey: 'nav.copyBetting', icon: UsersIcon },
    { href: '/social-leaderboard', labelKey: 'nav.socialLb', icon: SendIcon },
    { href: '/jackpot', labelKey: 'nav.jackpot', icon: Trophy },
    { href: '/staking', labelKey: 'nav.staking', icon: Coins },
    { href: '/booster-packs', labelKey: 'nav.boosterPacks', icon: Package },
    { href: '/sponsor', labelKey: 'nav.sponsor', icon: Crown },
    { href: '/b4p-launch', labelKey: 'nav.b4pLaunch', icon: Sparkles },
    { href: '/airdrop', labelKey: 'nav.airdrop', icon: Gift },
];

function buildAllLinks(t, isAdmin) {
    const links = NAV_LINKS.map(l => ({
        to: l.href, icon: l.icon, label: t(l.labelKey),
        testId: `nav-${l.href.replace('/', '')}`,
    }));
    links.push({ to: '/leaderboard/xp', icon: Sparkles, label: t('nav.xp'), testId: 'nav-xp-ranks' });
    links.push({ to: '/dashboard', icon: User, label: t('nav.dashboard'), testId: 'nav-dashboard' });
    if (isAdmin) {
        links.push({ to: '/admin', icon: Shield, label: t('nav.admin'), testId: 'nav-admin', accent: 'gold' });
    }
    return links;
}

export function Navbar() {
    const location = useLocation();
    const { t } = useTranslation();
    const { address } = useWeb3();
    const { theme, setTheme } = useTheme();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [b4pBalance, setB4pBalance] = useState(null);

    const isAdmin = address?.toLowerCase() === ADMIN_ADDRESS.toLowerCase();
    const allLinks = buildAllLinks(t, isAdmin);

    useEffect(() => {
        if (!address) { setB4pBalance(null); return; }
        const fetchBalance = async () => {
            try {
                const res = await fetch(`${API_URL}/api/b4p/balance/${address}`);
                if (res.ok) {
                    const data = await res.json();
                    setB4pBalance(data.balance);
                }
            } catch (e) { /* silent */ }
        };
        fetchBalance();
        const interval = setInterval(fetchBalance, 30000);
        return () => clearInterval(interval);
    }, [address]);

    return (
        <nav
            className="sticky top-0 z-50 bg-[#0A0A0C]/80 backdrop-blur-2xl border-b border-white/[0.06]"
            data-testid="navbar"
            role="navigation"
            aria-label="Main navigation"
        >
            <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-6">
                {/* === ROW 1 — Logo + Nav Links === */}
                <div className="flex items-center gap-3 h-16">
                    <Link to="/" className="flex items-center gap-3 group shrink-0" data-testid="logo-link" aria-label="Bet4Peer home">
                        <img src={BET4PEER_LOGO} alt="Bet4Peer" className="h-10 w-10 rounded-lg object-cover" />
                        <span className="font-bold text-xl tracking-tight hidden sm:inline">
                            <span className="text-white">Bet</span>
                            <span className="text-[#00FF66]">4</span>
                            <span className="text-white">Peer</span>
                        </span>
                    </Link>

                    <div className="hidden lg:flex items-center justify-between gap-1 ml-6 flex-1 min-w-0 overflow-x-auto scrollbar-hide">
                        {allLinks.map((link) => (
                            <NavLinkItem
                                key={link.to}
                                to={link.to}
                                icon={link.icon}
                                label={link.label}
                                isActive={location.pathname === link.to}
                                testId={link.testId}
                                accent={link.accent || 'green'}
                            />
                        ))}
                    </div>

                    <Button
                        variant="ghost"
                        size="icon"
                        className="lg:hidden text-white ml-auto"
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        data-testid="mobile-menu-button"
                        aria-expanded={mobileMenuOpen}
                        aria-controls="mobile-nav"
                        aria-label={mobileMenuOpen ? t('nav.closeMenu') : t('nav.openMenu')}
                    >
                        {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </Button>
                </div>

                {/* === ROW 2 — Controls (desktop only) === */}
                <div className="hidden lg:flex items-center justify-end gap-3 h-12 border-t border-white/10">
                    <div className="hidden sm:flex">
                        <ThemeButtons theme={theme} setTheme={setTheme} variant="desktop" />
                    </div>
                    <LanguageSelector />
                    <NavbarWalletSection b4pBalance={b4pBalance} />
                </div>

                {/* Mobile Navigation */}
                {mobileMenuOpen && (
                    <div className="lg:hidden py-4 border-t border-white/5" id="mobile-nav" role="menu">
                        <div className="flex flex-col gap-1">
                            {allLinks.map((link) => (
                                <NavLinkItem
                                    key={link.to}
                                    to={link.to}
                                    icon={link.icon}
                                    label={link.to === '/admin' ? t('nav.adminPanel') : link.label}
                                    isActive={location.pathname === link.to}
                                    onClick={() => setMobileMenuOpen(false)}
                                    variant="mobile"
                                    accent={link.accent || 'green'}
                                />
                            ))}
                            <div className="flex items-center gap-2 px-4 py-3">
                                <span className="text-white/40 text-sm mr-auto">{t('nav.theme')}</span>
                                <ThemeButtons theme={theme} setTheme={setTheme} variant="mobile" />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </nav>
    );
}
